import { ft } from '../core/units.js';
import { resolveHorizontal, supportHeight } from '../core/collision.js';
import { createRng, hashSeed } from './rng.js';
import { SCATTER } from '../data/scatter.js';

/**
 * Puts the hotel's clutter where it lives.
 *
 * Seeded, so the same run always finds the fire extinguisher in the same place.
 * Positions are rejected until one is found that a body could stand in, which
 * is a cheap and completely general way to keep an orange out of a wall — the
 * alternative is hand-placing sixty things and re-hand-placing them every time
 * a room moves.
 *
 * It spawns entities and nothing else. Whether any of them matters is decided
 * by the rules that act on tags, never here.
 */

/** Object radius, roughly, for the "does this fit" test. */
const CLEARANCE = 0.35;

export function scatterInto({ entities, level, world, seed = 'lion', plan = SCATTER }) {
  const rng = createRng(hashSeed(seed, 'scatter'));
  const rooms = new Map(level.rooms.map((room) => [room.id, room]));
  const placed = [];

  function standable(x, z) {
    const floor = supportHeight(x, z, CLEARANCE, 0, world);
    if (floor > 0.35) return false;
    return (
      resolveHorizontal({ x, z }, CLEARANCE, floor, floor + 1.4, world).contacts.length === 0
    );
  }

  /** A spot inside a room's floor that a thing can sit on. */
  function spotIn(roomId) {
    const room = rooms.get(roomId);
    if (!room) return null;
    const [x0, z0, x1, z1] = room.rect;
    for (let attempt = 0; attempt < 40; attempt += 1) {
      // Inset from the walls so nothing spawns jammed into a corner.
      const x = ft(x0 + 2 + rng.next() * Math.max(0, x1 - x0 - 4));
      const z = ft(z0 + 2 + rng.next() * Math.max(0, z1 - z0 - 4));
      if (standable(x, z)) return { x, y: 0, z };
    }
    return null;
  }

  /**
   * Nudges a hand-placed spot out of whatever it landed in.
   *
   * Hand coordinates go stale the moment a prop moves or a room changes shape,
   * and a fire extinguisher inside a shelf is invisible rather than obviously
   * wrong. Searching outward keeps the data forgiving without letting it be
   * silently wrong.
   */
  function nearestClear(x, z) {
    if (standable(x, z)) return { x, y: 0, z };
    for (let ring = 1; ring <= 6; ring += 1) {
      const step = ft(ring * 1.5);
      for (let i = 0; i < 8; i += 1) {
        const angle = (i / 8) * Math.PI * 2;
        const cx = x + Math.cos(angle) * step;
        const cz = z + Math.sin(angle) * step;
        if (standable(cx, cz)) return { x: cx, y: 0, z: cz };
      }
    }
    return null;
  }

  for (const entry of plan) {
    if (entry.at) {
      const position = nearestClear(ft(entry.at[0]), ft(entry.at[1]));
      if (position) placed.push(entities.spawn({ kind: entry.kind, position }));
      continue;
    }
    for (let i = 0; i < (entry.count ?? 1); i += 1) {
      const position = spotIn(entry.in);
      if (!position) continue;
      placed.push(entities.spawn({ kind: entry.kind, position }));
    }
  }

  return placed;
}
